import { queryKeys } from './keys';

/**
 * Invalidate the "which collections is this item in" membership cache
 * (CollectionPickerModal, COL-96) for one or more items, after any mutation
 * that changes collection membership — adding, creating-and-adding, or
 * removing.
 *
 * Centralized here rather than each mutation site hand-writing
 * `queryClient.invalidateQueries({ queryKey: queryKeys.collectionsForItem(...) })`
 * per item (as CollectionPickerModal's two add mutations and
 * CollectionDetail's remove mutation each did independently before this) —
 * a future caller of addItemsToCollection/removeItemsFromCollection can't
 * forget the exact key shape and leave the picker showing stale membership.
 * Matches the `fetchItemDetail` precedent in `queries/items.js`: a single
 * cache-key-shaped helper reused across call sites, not a wrapper around
 * the mutations themselves (this codebase declares `useMutation` inline in
 * each component — see Gallery.jsx, CollectionDetail.jsx, MyCollections.jsx
 * — so only the repeated invalidation logic is extracted, not that pattern).
 *
 * One `invalidateQueries({ predicate })` call regardless of item count,
 * rather than one call per item (each of which walks the whole query cache
 * to find partial-key matches).
 *
 * @param {import('@tanstack/react-query').QueryClient} queryClient
 * @param {string|undefined} userId
 * @param {Array<{item_id: string, item_type: string}>} items
 */
export function invalidateCollectionsForItems(queryClient, userId, items) {
  if (!items || items.length === 0) return;

  const targetKeys = new Set(
    items.map((item) => JSON.stringify(queryKeys.collectionsForItem(userId, item.item_id, item.item_type)))
  );
  queryClient.invalidateQueries({
    predicate: (query) => targetKeys.has(JSON.stringify(query.queryKey)),
  });
}
